// 发型示例图：列表来自服务端 R2；缺的可以一键生成（每张约 ¥0.25，一次性，所有设备共用）

import { useEffect, useState } from "preact/hooks";
import { SAMPLE_VERSION, STYLES } from "../../shared/styles";
import { describeError, generateSampleImage } from "./api";
import { blobToCanvas, canvasToBlob } from "./image";
import { getAccessCode } from "./jobs";

interface SampleState {
  ids: Set<string>;
  running: boolean;
  done: number;
  total: number;
  failed: number;
  lastError?: string;
}

let state: SampleState = { ids: new Set(), running: false, done: 0, total: 0, failed: 0 };
const subs = new Set<() => void>();

function set(patch: Partial<SampleState>) {
  state = { ...state, ...patch };
  for (const s of subs) s();
}

export function useSamples(): SampleState {
  const [, force] = useState(0);
  useEffect(() => {
    const fn = () => force((n) => n + 1);
    subs.add(fn);
    return () => {
      subs.delete(fn);
    };
  }, []);
  return state;
}

export function sampleUrl(id: string): string {
  return `/api/sample/${id}?v=${SAMPLE_VERSION}`;
}

export async function loadSamples() {
  try {
    const r = await fetch("/api/samples");
    if (!r.ok) return;
    const body = (await r.json()) as { ids: string[] };
    set({ ids: new Set(body.ids) });
  } catch {
    /* 离线时忽略 */
  }
}

export function missingSamples(): string[] {
  return STYLES.filter((s) => !state.ids.has(s.id)).map((s) => s.id);
}

/** 依次生成缺少的示例图（同时 2 张），每张出来就刷新卡片 */
export async function generateMissingSamples() {
  if (state.running) return;
  const todo = missingSamples();
  if (!todo.length) return;
  set({ running: true, done: 0, total: todo.length, failed: 0, lastError: undefined });
  const code = getAccessCode();
  const queue = [...todo];
  const worker = async () => {
    for (;;) {
      const id = queue.shift();
      if (!id) return;
      try {
        const { blob } = await generateSampleImage(id, code);
        // 缩成 420px 宽的小图再存，列表加载快
        const small = await blobToCanvas(blob, 560);
        const jpeg = await canvasToBlob(small, "image/jpeg", 0.84);
        const put = await fetch(`/api/sample/${id}`, {
          method: "PUT",
          body: jpeg,
          headers: { "content-type": "image/jpeg", "x-access-code": code },
        });
        if (!put.ok) throw new Error(`保存失败（${put.status}）`);
        const ids = new Set(state.ids);
        ids.add(id);
        set({ ids, done: state.done + 1 });
      } catch (e) {
        set({ failed: state.failed + 1, done: state.done + 1, lastError: describeError(e) });
      }
    }
  };
  await Promise.all([worker(), worker()]);
  set({ running: false });
}
